import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import omit from 'lodash-es/omit';
import pickBy from 'lodash-es/pickBy';
import sortBy from 'lodash-es/sortBy';
import indexOf from 'lodash-es/indexOf';
import { ToastrService } from 'ngx-toastr';
import { Type } from '../../components/form-builder/enums/type.enum';
import { EdlForm } from '../../components/form-builder/models/edl-form.model';
import { EdlOptionValue } from '../../components/form-builder/models/edl-option-value.model';
import { EdlQuestion } from '../../components/form-builder/models/edl-question.model';
import { EdlSection } from '../../components/form-builder/models/edl-section.model';
import { Graph } from '../../data-structures/graph';
import { EdlUserForm } from '../../model/edl-user-form.model';
import { FileAttachment } from '../../model/file-attachment.model';
import { DateHelpers } from '@edl-development/edl-common';
import { FileService } from '../../services/file.service';
import { QuestionnaireService } from '../../services/questionnaire.service';
import { UserFormStatus } from '../../supervision-management/compliance-items/compliance-item/enums/user-form-status.enum';
import { CommonArrayValidators, CommonValidators } from '@edl-development/edl-common';
import { firstValueFrom } from 'rxjs';

const QUESTION_FORM_ID_PREFIX = 'Question_';
@Component({
    selector: 'questionnaire-form-displayer',
    templateUrl: './questionnaire-form-displayer.component.html',
    styleUrls: ['./questionnaire-form-displayer.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class QuestionnaireFormDisplayerComponent implements OnInit {
    @Input() userFormDetails: { QuestionnaireDetails: EdlForm, UserForm: EdlUserForm, UserFormHistory:[any] };
    @Input() masterComplete: Boolean = false;
    @Input() close: Function;
    @Input() printUrl: String;
    @Input() comfirmOnClose: Boolean = false;
    @Input() closeOnComplete: Boolean = true;

    // Modes
    @Input() modalMode: Boolean = false;
    @Input() previewMode: Boolean = false;
    @Input() printMode: Boolean = false;

    @Output() onSubmissionComplete = new EventEmitter<{ success: boolean, isQuestionnaireComplete: boolean }>();

    PREVIEW_QUESTIONNAIRE = "Preview Questionnaire"
    title: string = "";
    itemTitle: string = "";
    userName: string = "";
    busy: boolean;
    shouldRecalculate: boolean;

    questionnaire: EdlForm;
    userForm: EdlUserForm;

    DateHelpers = DateHelpers;
    Type = Type;
    UserFormStatus = UserFormStatus;
    form: UntypedFormGroup;
    overrideForm:boolean = false;

    calculationGraph: Graph;
    
    checkboxQuestions: Object;
    checkboxQuestionResponses: Object;
    signaturePadOptions: Object;
    signatureFormKeys: string[];
    signatureOriginalValues: Object;
    IsHistory:boolean;
    IsMasterComplete:Boolean;
    originalSignatures: Object;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private questionnaireService: QuestionnaireService,
        private toastr: ToastrService,
        private fileService: FileService,
    ) {
        this.calculationGraph = new Graph();
    }

    ngOnInit() {
        this.busy = false;
        this.shouldRecalculate = false;
        this.signaturePadOptions = {
            'canvasWidth': 600,
            'canvasHeight': 200
        };
        
        this.populateForm();

        this.title = this.previewMode ? this.PREVIEW_QUESTIONNAIRE : this.questionnaire.Title;
    }


    populateForm = () => {
        this.IsHistory = this.userFormDetails.UserForm ? this.userFormDetails.UserForm.IsHistory : false;
        this.IsMasterComplete = this.masterComplete;
        this.checkboxQuestions = {};
        this.checkboxQuestionResponses = {};
        this.questionnaire = this.userFormDetails.QuestionnaireDetails;
        this.userForm = this.userFormDetails.UserForm;
        this.userName = this.userForm ? this.userForm.UserName : '';
        this.itemTitle = this.userForm ? this.userForm.ItemTitle : '';
        this.signatureFormKeys = [];
        this.signatureOriginalValues = {};

        this.questionnaire.Sections = sortBy(this.questionnaire.Sections, ['DisplayOrder']);
        this.questionnaire.Sections.map((section: EdlSection) => {
            section.Questions = sortBy(section.Questions, ['DisplayOrder']);
        });

        this.calculationGraph = new Graph();


        this.form = this.formBuilder.group({
            UserFormId: [this.userForm ? this.userForm.UserFormId : null, []],
            FormComplete: [this.userForm ? this.userForm.Status === UserFormStatus.COMPLETED : false, []]
        });
        
        this.questionnaire.Sections.forEach((section: EdlSection) => {
            section.Questions.forEach((question: EdlQuestion) => {
                const questionID = this.generateQuestionFormGroupId(question.QuestionID.toString())
                if (question.TypeID === Type.CHECKBOX) {
                    let array: UntypedFormArray = new UntypedFormArray([]);
                    let selected = null;

                    if (this.userForm && this.userForm.FormAnswers.hasOwnProperty(question.QuestionID)) {
                        selected = eval(this.userForm.FormAnswers[question.QuestionID]);
                    }

                    this.checkboxQuestions[questionID] = array;
                    this.form.addControl(questionID, array);

                    question.AnswerConfig.OptionValues.map((optionValue: EdlOptionValue) => {
                        optionValue.control = new UntypedFormControl(optionValue.optionValue);

                        if (indexOf(selected, optionValue.optionValue) >= 0) {
                            optionValue['isChecked'] = true;
                            array.push(optionValue.control);
                        }
                    });
                }
                else if (question.TypeID === Type.SIGNATURE) {
                    let control: UntypedFormControl = new UntypedFormControl(null, []);
                    this.form.addControl(questionID, control);
                    this.signatureFormKeys.push(questionID);

                    if (this.userForm && this.userForm.FormAnswers.hasOwnProperty(question.QuestionID) && this.userForm.FormAnswers[question.QuestionID] && this.userForm.FormAnswers[question.QuestionID].length > 0) {
                        this.fileService
                            .getFile(this.userForm.FormAnswers[question.QuestionID])
                            .subscribe((response: string) => {
                                this.signatureOriginalValues[response] = response;
                                this.form.get(questionID).setValue(response);
                            });
                        // HttpErrorHandler interceptor displays signature loading errors automatically
                    }
                    
                }
                else {
                    let value = null;

                    if (this.userForm && this.userForm.FormAnswers.hasOwnProperty(question.QuestionID)) {
                        value = this.userForm.FormAnswers[question.QuestionID];
                    }

                    let control: UntypedFormControl = new UntypedFormControl(value, []);
                    this.form.addControl(questionID, control);
                }

                const questionFormGroup = this.form.get(questionID);
                if (questionFormGroup && questionFormGroup instanceof AbstractControl) {
                    this.calculationGraph.addNode(questionID, questionFormGroup)
                }
            });
        });

        this.questionnaire.Sections.forEach((section: EdlSection) => {
            section.Questions.forEach((question: EdlQuestion) => {
                if (question.TypeID === Type.CALCULATION && question.LinkedQuestionIDs &&  question.LinkedQuestionIDs.length > 0) {
                    const parentNodeId = this.generateQuestionFormGroupId(question.QuestionID.toString());
                    question.LinkedQuestionIDs.forEach((childId: number) => {
                        const childNodeId = this.generateQuestionFormGroupId(childId.toString());
                        this.calculationGraph.addEdge(parentNodeId, childNodeId);
                    });
                }
            });
        });

        this.questionnaire.Sections.forEach((section: EdlSection) => {
            section.Questions.forEach((question: EdlQuestion) => { 
                this.setValidators(question);
            });
        });
    }

    setValidators = (question: EdlQuestion ) => {
        const validators = [];
        const id = this.generateQuestionFormGroupId(question.QuestionID.toString());
        const form = this.form.get(id);
        if (question.Required) {
            validators.push(question.TypeID === Type.CHECKBOX ? CommonArrayValidators.emptyArrayValidator() : Validators.required);
        }

        if (question.TypeID === Type.CALCULATION || this.calculationGraph.hasParents(id)) {
            validators.push(CommonValidators.isNumber());
        }

        form.setValidators(validators);
        form.updateValueAndValidity();
    }

    generateQuestionFormGroupId = (questinoID: string) => {
        return QUESTION_FORM_ID_PREFIX + questinoID.toString()
    }

    convertQuestionFormIdToId = (questionFormID: string) => {
        return questionFormID.replace(QUESTION_FORM_ID_PREFIX, '');
    }

    submit({ value, valid }: { value: any, valid: boolean }, isDraft: boolean) {
        this.busy = true;

        if (valid && !isDraft) {
            value.FormComplete = true;
        }

        // filter out signatures
        const valuesNoSignatures = value ? omit(value, this.signatureFormKeys) : value;
        const valuesSignaturesOnly = value ? pickBy(value, (v, k) => this.signatureFormKeys.indexOf(k) !== -1) : {};

        firstValueFrom(
            this.questionnaireService.completeQuestionnaire(valuesNoSignatures)
        ).then(() => {
            const questionFormIds = valuesSignaturesOnly && !this.isFormLocked() && !this.overrideForm ? 
                Object.keys(valuesSignaturesOnly) :
                [];
        
            return this.uploadSignatures(questionFormIds, this.questionnaire.PortalID, value);
        }).finally(() => this.busy = false);
    }

    private uploadSignatures(questionFormIds: string[], portalId: number, value: any): Promise<any> {
        if (questionFormIds.length > 0) {
            const questionFormId = questionFormIds.pop();
            const dataUrl = value[questionFormId];
            const questionId = parseInt(this.convertQuestionFormIdToId(questionFormId));
            const fileId: string = this.userForm.FormAnswers.hasOwnProperty(questionId) ? this.userForm.FormAnswers[questionId] : null;

            // Delete Signature if cleared
            if (fileId && !dataUrl) {
                const file = new FileAttachment();
                file.FileID = fileId;

                return firstValueFrom(
                    this.fileService.deleteFile(file)
                ).then(() => this.uploadSignatures(questionFormIds, portalId, value));

            // Update Signature if changed
            } else if (this.signatureOriginalValues[dataUrl] !== dataUrl && dataUrl) {
                let blob: Blob = this.fileService.dataURItoBlob(dataUrl);

                return firstValueFrom(
                    this.fileService.uploadSignature({ signature: blob, type: 4, typeId: this.userForm.UserFormId, subTypeId: questionId, portalId, fileId})
                ).then(() => this.uploadSignatures(questionFormIds, portalId, value));
            
            // Go to the next signature if the signature was not changed
            } else {
                return this.uploadSignatures(questionFormIds, portalId, value);
            }
        }
        else {
            return this.complete(value.FormComplete);
        }
    }

    loadForm(){
        this.questionnaireService
            .getUserForm(this.userFormDetails.UserForm.UserFormId)
            .subscribe((response:any) => {
                
                this.userFormDetails = response;
                this.populateForm();

            });


    }

    loadHistory(history:number){
        this.questionnaireService
            .getUserFormHistory(this.userForm.UserFormId, history)
            .subscribe((response: { QuestionnaireDetails: EdlForm, UserForm: EdlUserForm, UserFormHistory:[any] }) => {
                this.userFormDetails.UserForm = response.UserForm;
                this.populateForm();

            });

    }

    loadCurrent(){
        return firstValueFrom(
            this.questionnaireService.getUserForm(this.userForm.UserFormId)
        ).then((response: { QuestionnaireDetails: EdlForm, UserForm: EdlUserForm, UserFormHistory:[any] }) => {
                this.userFormDetails = response;
                this.populateForm();
        });  
    }
    
    clearSignature(questionId: number) {
        const formId = this.generateQuestionFormGroupId(questionId.toString());
        const signature = this.form.get(formId);

        if (signature) {
            signature.setValue(null);
            signature.markAsDirty();
        }
    }

    unlockForm(){
        this.userForm.Status = 1;
        this.overrideForm = true;
    }

    complete(isComplete: boolean) {
        this.form.markAsPristine();

        this.onSubmissionComplete.emit({ success: true, isQuestionnaireComplete: isComplete });

        if (isComplete) {
            this.toastr.success('Questionnaire completed successfully.');
            if(this.close && this.closeOnComplete) {
                this.close()
            } else {
                return this.loadCurrent()
            }
        }
        else {
            this.toastr.success('Questionnaire draft saved successfully.');
        }

        return Promise.resolve();
    }

    isFormLocked = () => {
        return this.printMode || (this.userForm && this.userForm.Status === UserFormStatus.LOCKED) || this.IsMasterComplete;
    }

    recalculate = (questionId: number) => {
        const formId = this.generateQuestionFormGroupId(questionId.toString());
        if (this.calculationGraph.hasParents(formId)) {
            if (!this.busy) {
                this.fetchCalculatedValues();
            } else {
                this.shouldRecalculate = true;
            }

        }
    }

    private fetchCalculatedValues = () => {
        this.busy = true;

        const rawJsonForm = this.form.getRawValue();

        // Filter out signatures
        const jsonForm = rawJsonForm ? omit(rawJsonForm, this.signatureFormKeys) : rawJsonForm;

        jsonForm.FormID = this.questionnaire.FormID;
        this.questionnaireService
            .recalculate(jsonForm)
            .subscribe((response:any) => {
                this.busy = false;

                if (response) {
                    Object.keys(this.calculationGraph.getNodes()).forEach((questionFormKey: string) => {
                        const questionId = this.convertQuestionFormIdToId(questionFormKey);
                        if (this.calculationGraph.hasChildren(questionFormKey) && response.hasOwnProperty(questionId)) {
                            const node = this.calculationGraph.getNode(questionFormKey);
                            node.setValue(response[questionId]);
                            node.markAsTouched();
                        }
                    });
                }

                if (this.shouldRecalculate) {
                    this.shouldRecalculate = false;
                    this.fetchCalculatedValues();
                }

                this.form.markAsDirty();

            }, () => {
                this.busy = false;
            });
    }
}